import { useCallback, useState } from "react";
import { movieRepository } from "../api/movieRepository";

/** @typedef {import("../types").Movie} Movie */

/**
 * Estado y carga del catálogo de películas (listado inicial + búsqueda).
 * Recibe el repositorio por parámetro para poder inyectar un doble en tests.
 * @param {typeof movieRepository} [repository]
 */
export function useMovieCatalog(repository = movieRepository) {
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState("");

  /** @returns {Promise<Movie[]>} */
  const loadInitialMovies = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const movieList = await repository.discoverMovies();
      setMovies(movieList);
      return movieList;
    } catch (loadError) {
      console.error("No se pudo cargar el catálogo de películas.", loadError);
      setError("No pudimos cargar películas en este momento.");
      return [];
    } finally {
      setLoading(false);
    }
  }, [repository]);

  /** @param {string} searchTerm @returns {Promise<Movie[]>} */
  const searchMovies = useCallback(
    async (searchTerm) => {
      const normalizedSearch = searchTerm.trim();

      if (!normalizedSearch) {
        return loadInitialMovies();
      }

      setSearchLoading(true);
      setError("");

      try {
        const movieResults = await repository.searchMoviesByQuery(normalizedSearch);
        setMovies(movieResults);
        return movieResults;
      } catch (searchError) {
        console.error("No se pudo completar la búsqueda de películas.", searchError);
        setError("No pudimos completar la búsqueda.");
        return [];
      } finally {
        setSearchLoading(false);
      }
    },
    [repository, loadInitialMovies]
  );

  return { movies, loading, searchLoading, error, loadInitialMovies, searchMovies };
}
